import React from 'react';
import {
  FaSearch,
  FaFilter,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaTrash,
  FaClock,
  FaTruck,
  FaCheckCircle,
  FaBan,
} from 'react-icons/fa';
import { OrderStatus } from '../../utils/orderUtils';

// Order Statistics Component
interface OrderStatsProps {
  stats: {
    total: number;
    pending: number;
    preparing: number;
    onTheWay: number;
    completed: number;
  };
}

export const OrderStatistics: React.FC<OrderStatsProps> = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
      <div className="text-sm text-gray-600">Toplam Sipariş</div>
    </div>
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
      <div className="text-sm text-gray-600">Bekleyen</div>
    </div>
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="text-2xl font-bold text-blue-600">{stats.preparing}</div>
      <div className="text-sm text-gray-600">Hazırlanıyor</div>
    </div>
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="text-2xl font-bold text-purple-600">{stats.onTheWay}</div>
      <div className="text-sm text-gray-600">Yolda</div>
    </div>
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
      <div className="text-sm text-gray-600">Tamamlandı</div>
    </div>
  </div>
);

// Order Filters Component
interface OrderFiltersProps {
  searchTerm: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
}

export const OrderFilters: React.FC<OrderFiltersProps> = ({
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
}) => (
  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Ad, soyad, departman veya ürün ara..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>
      <div className="relative">
        <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <select
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          value={statusFilter}
          onChange={e => onStatusFilterChange(e.target.value)}
        >
          <option value="all">Tüm Durumlar</option>
          <option value="pending">Bekliyor</option>
          <option value="preparing">Hazırlanıyor</option>
          <option value="on-the-way">Yolda</option>
          <option value="completed">Tamamlandı</option>
          <option value="cancelled">İptal Edildi</option>
        </select>
      </div>
    </div>
  </div>
);

// Order List Header Component
interface OrderListHeaderProps {
  count: number;
}

export const OrderListHeader: React.FC<OrderListHeaderProps> = ({ count }) => (
  <div className="p-6 border-b border-gray-100">
    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
      <FaSearch className="text-blue-500" />
      Sipariş Listesi ({count} sipariş)
    </h3>
  </div>
);

// Empty Orders State Component
interface EmptyOrdersStateProps {
  searchTerm: string;
  statusFilter: string;
}

export const EmptyOrdersState: React.FC<EmptyOrdersStateProps> = ({ searchTerm, statusFilter }) => (
  <div className="text-center py-12 text-gray-500">
    {searchTerm || statusFilter !== 'all'
      ? 'Arama kriterlerinize uygun sipariş bulunamadı'
      : 'Henüz sipariş bulunmuyor'}
  </div>
);

// Bulk Actions Component
interface BulkActionsProps {
  selectedCount: number;
  bulkUpdating: boolean;
  onBulkStatusChange: (status: OrderStatus) => void;
  onBulkDelete: () => void;
  onClearSelection: () => void;
}

export const BulkActions: React.FC<BulkActionsProps> = ({
  selectedCount,
  bulkUpdating,
  onBulkStatusChange,
  onBulkDelete,
  onClearSelection,
}) => {
  if (selectedCount === 0) {
    return null;
  }

  const actionButtons = [
    {
      label: 'Hazırlanıyor',
      status: 'preparing' as OrderStatus,
      icon: FaClock,
      gradient: 'from-blue-500 to-blue-600',
      hoverGradient: 'from-blue-600 to-blue-700',
      shadowColor: 'shadow-blue-500/25',
    },
    {
      label: 'Yolda',
      status: 'on-the-way' as OrderStatus,
      icon: FaTruck,
      gradient: 'from-purple-500 to-purple-600',
      hoverGradient: 'from-purple-600 to-purple-700',
      shadowColor: 'shadow-purple-500/25',
    },
    {
      label: 'Tamamlandı',
      status: 'completed' as OrderStatus,
      icon: FaCheckCircle,
      gradient: 'from-green-500 to-green-600',
      hoverGradient: 'from-green-600 to-green-700',
      shadowColor: 'shadow-green-500/25',
    },
    {
      label: 'İptal Edildi',
      status: 'cancelled' as OrderStatus,
      icon: FaBan,
      gradient: 'from-red-500 to-red-600',
      hoverGradient: 'from-red-600 to-red-700',
      shadowColor: 'shadow-red-500/25',
    },
  ];

  return (
    <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 mb-6 backdrop-blur-sm">
      {/* Header Section */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
            <FaCheck className="text-white text-lg" />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg">
            {selectedCount}
          </div>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">{selectedCount} Sipariş Seçildi</h3>
          <p className="text-gray-600">Seçili siparişler için toplu işlemler yapabilirsiniz</p>
        </div>
      </div>

      {/* Status Change Actions */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
          📋 Durum Değiştir
        </h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {actionButtons.map(
            ({ label, status, icon: Icon, gradient, hoverGradient, shadowColor }) => (
              <button
                key={status}
                onClick={() => onBulkStatusChange(status)}
                disabled={bulkUpdating}
                className={`
                group relative overflow-hidden
                bg-gradient-to-r ${gradient} hover:${hoverGradient}
                text-white font-semibold
                px-4 py-3 rounded-xl
                transform transition-all duration-200
                hover:scale-105 hover:shadow-lg ${shadowColor}
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              `}
              >
                <div className="flex items-center justify-center gap-2">
                  {bulkUpdating ? (
                    <FaSpinner className="animate-spin text-lg" />
                  ) : (
                    <>
                      <Icon className="text-lg" />
                      <span className="text-sm">{label}</span>
                    </>
                  )}
                </div>

                {/* Shine effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                </div>
              </button>
            )
          )}
        </div>
      </div>

      {/* Danger Actions */}
      <div className="border-t border-gray-100 pt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
          ⚠️ Tehlikeli İşlemler
        </h4>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onBulkDelete}
            disabled={bulkUpdating}
            className="
              group flex-1 bg-gradient-to-r from-red-500 to-red-600
              hover:from-red-600 hover:to-red-700
              text-white font-semibold px-6 py-3 rounded-xl
              transform transition-all duration-200
              hover:scale-105 hover:shadow-lg shadow-red-500/25
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
            "
          >
            <div className="flex items-center justify-center gap-2">
              {bulkUpdating ? (
                <FaSpinner className="animate-spin text-lg" />
              ) : (
                <>
                  <FaTrash className="text-lg" />
                  <span>Toplu Sil ({selectedCount} sipariş)</span>
                </>
              )}
            </div>
          </button>

          <button
            onClick={onClearSelection}
            disabled={bulkUpdating}
            className="
              group flex-1 bg-gradient-to-r from-gray-500 to-gray-600
              hover:from-gray-600 hover:to-gray-700
              text-white font-semibold px-6 py-3 rounded-xl
              transform transition-all duration-200
              hover:scale-105 hover:shadow-lg shadow-gray-500/25
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500
            "
          >
            <div className="flex items-center justify-center gap-2">
              <FaTimes className="text-lg" />
              <span>Seçimi Temizle</span>
            </div>
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {bulkUpdating && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-xl shadow-lg">
            <FaSpinner className="animate-spin text-blue-500 text-xl" />
            <span className="font-semibold text-gray-700">İşlem yapılıyor...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Select All Checkbox Component
interface SelectAllCheckboxProps {
  isAllSelected: boolean;
  onToggleAll: () => void;
  totalCount: number;
}

export const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({
  isAllSelected,
  onToggleAll,
  totalCount,
}) => {
  return (
    <div className="flex items-center gap-2 mb-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={onToggleAll}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
        <span className="text-sm font-medium text-gray-700">Tümünü Seç ({totalCount} sipariş)</span>
      </label>
    </div>
  );
};

// Order Row Checkbox Component
interface OrderRowCheckboxProps {
  orderId: number;
  isSelected: boolean;
  onToggle: (orderId: number) => void;
}

export const OrderRowCheckbox: React.FC<OrderRowCheckboxProps> = ({
  orderId,
  isSelected,
  onToggle,
}) => {
  return (
    <div className="flex items-center justify-center">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(orderId)}
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
      />
    </div>
  );
};
